import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../../services/api'
import './Player.css'
import { MainLayout } from '../../Layout/MainLayout';

interface Video { id: string; title: string; order: number; durationSec: number; bunnyVideoId: string; bunnyLibraryId: string }
interface Quiz { id: string; title: string }
interface Course { id: string; title: string; description: string; area: { name: string }; videos: Video[]; quizzes: Quiz[] }
interface Progress { videoId: string; completed: boolean; assistedSeconds: number }

export function Player() {
    const { courseId } = useParams()
    const navigate = useNavigate()
    const [course, setCourse] = useState<Course | null>(null)
    const [progress, setProgress] = useState<Progress[]>([])
    const [currentVideo, setCurrentVideo] = useState<Video | null>(null)
    const [loading, setLoading] = useState(true)
    const loadProgress = async () => {
        if (!courseId) return;

        const progressRes = await api.get(`/progress/course/${courseId}`);
        setProgress(progressRes.data.videos || []);
    };
    useEffect(() => {
        if (!courseId) return
        Promise.all([
            api.get(`/course/${courseId}`),
            api.get(`/progress/course/${courseId}`)
        ]).then(([courseRes, progressRes]) => {
            const c = courseRes.data.data
            const progressData = progressRes.data.videos || []
            setCourse(c)
            setProgress(progressData)

            // Encontra o primeiro vídeo não concluído
            const sortedVideos = [...(c.videos || [])].sort((a, b) => a.order - b.order)
            const nextVideo = sortedVideos.find(v => {
                const p = progressData.find((pr: Progress) => pr.videoId === v.id)
                return !p?.completed
            })

            setCurrentVideo(nextVideo || sortedVideos[0] || null)
        }).finally(() => setLoading(false))
    }, [courseId])

    // useEffect(() => {
    //     if (!currentVideo) return

    //     const iframe = document.querySelector('iframe') as HTMLIFrameElement
    //     if (!iframe) return

    //     // @ts-ignore
    //     const player = new playerjs.Player(iframe)

    //     player.on('ready', () => {
    //         player.on('pause', () => {
    //             // @ts-ignore
    //             player.getCurrentTime((currentTime: number) => {
    //                 api.post('/progress', {
    //                     videoId: currentVideo.id,
    //                     assistedSeconds: Math.floor(currentTime),
    //                     completed: false,
    //                 }).catch(() => { })
    //             })
    //         })

    //         player.on('ended', () => {
    //             api.post('/progress', {
    //                 videoId: currentVideo.id,
    //                 assistedSeconds: currentVideo.durationSec ?? 0,
    //                 completed: true,
    //             }).then(() => {
    //                 setProgress(prev => {
    //                     const exists = prev.find(p => p.videoId === currentVideo.id)
    //                     if (exists) return prev.map(p =>
    //                         p.videoId === currentVideo.id ? { ...p, completed: true } : p
    //                     )
    //                     return [...prev, {
    //                         videoId: currentVideo.id,
    //                         completed: true,
    //                         assistedSeconds: currentVideo.durationSec ?? 0
    //                     }]
    //                 })
    //             }).catch(() => { })
    //         })
    //     })

    //     return () => {
    //         try { player.off('pause'); player.off('ended') } catch { }
    //     }
    // }, [currentVideo])
    useEffect(() => {
        if (!currentVideo) return

        const iframe = document.querySelector('iframe') as HTMLIFrameElement
        if (!iframe) return

        // @ts-ignore
        const player = new playerjs.Player(iframe)
        let hasEnded = false

        player.on('ready', () => {
            player.on('pause', () => {
                player.on('pause', () => console.log('PAUSE disparou'))
                if (hasEnded) return

                // @ts-ignore
                player.getCurrentTime((currentTime: number) => {
                    if (hasEnded) return // re-checa: o 'ended' pode ter disparado enquanto isso era assíncrono
                    api.post('/progress', {
                        videoId: currentVideo.id,
                        assistedSeconds: Math.floor(currentTime),
                        completed: false,
                    }).catch(() => { })
                })
            })

            player.on('ended', () => {
                player.on('ended', () => console.log('ENDED disparou'))
                hasEnded = true
                api.post('/progress', {
                    videoId: currentVideo.id,
                    assistedSeconds: currentVideo.durationSec ?? 0,
                    completed: true,
                }).then(() => {
                    loadProgress()
                }).catch(() => { })
            })
        })
        return () => {
            try { player.off('pause'); player.off('ended') } catch { }
        }
    }, [currentVideo])

    function getVideoStatus(videoId: string) {
        const p = progress.find(p => p.videoId === videoId)
        if (p?.completed) return 'done'
        if ((p?.assistedSeconds ?? 0) > 0) return 'current'
        return 'todo'
    }

    async function markCompleted(videoId: string) {
        await api.post('/progress', { videoId, assistedSeconds: currentVideo?.durationSec ?? 0, completed: true })
        setProgress(prev => {
            const exists = prev.find(p => p.videoId === videoId)
            if (exists) return prev.map(p => p.videoId === videoId ? { ...p, completed: true } : p)
            return [...prev, { videoId, completed: true, assistedSeconds: currentVideo?.durationSec ?? 0 }]
        })
    }

    const completedCount = progress.filter(p => p.completed).length
    const totalVideos = course?.videos?.length ?? 0
    const allDone = completedCount >= totalVideos && totalVideos > 0
    const quiz = course?.quizzes?.[0]
    const currentProgress = progress.find(p => p.videoId === currentVideo?.id)
    const startTime = currentProgress?.assistedSeconds ?? 0
    if (loading) return <div className="container fade-in" style={{ paddingTop: 60, color: 'var(--text-3)', textAlign: 'center' }}>Carregando...</div>
    if (!course) return <div className="container">Curso não encontrado.</div>

    return (
        <MainLayout>
            <div className="container fade-in">
                <div className="breadcrumb">
                    <a onClick={() => navigate('/catalogo')}>Biblioteca</a>
                    <span className="sep">›</span>
                    <span>{course.area.name}</span>
                    <span className="sep">›</span>
                    <span>{course.title}</span>
                </div>

                <div className="player-layout">
                    <div className="player-main">
                        <div className="player-frame">
                            {currentVideo ? (
                                <iframe
                                    src={`https://iframe.mediadelivery.net/embed/${currentVideo.bunnyLibraryId}/${currentVideo.bunnyVideoId}?autoplay=false&responsive=true&enableControls=true&t=${startTime}`}
                                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
                                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                                    allowFullScreen
                                    referrerPolicy="origin"
                                />
                            ) : (
                                <>
                                    <div className="play-btn">▶</div>
                                    <div className="player-overlay-title serif">{course.title}</div>
                                </>
                            )}
                        </div>

                        <div className="player-info">
                            <h1 className="serif">{currentVideo?.title ?? course.title}</h1>
                            <div className="player-info-meta">
                                <span>{course.area?.name}</span>
                                <span className="dot" />
                                <span style={{ color: 'var(--teal)' }}>{completedCount}/{totalVideos} concluídos</span>
                            </div>
                            <p style={{ color: 'var(--text-2)', fontSize: 14 }}>{course.description}</p>

                            {currentVideo && getVideoStatus(currentVideo.id) !== 'done' && (
                                <button
                                    className="btn btn-ghost"
                                    style={{ marginTop: 18 }}
                                    onClick={() => markCompleted(currentVideo.id)}
                                >
                                    ✓ Marcar como concluído
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="player-side">
                        <div className="side-card">
                            <div className="side-card-head">
                                <h4 className="serif">Módulos</h4>
                                <span className="progress-text">{completedCount}/{totalVideos}</span>
                            </div>
                            {course.videos?.sort((a, b) => a.order - b.order).map(v => {
                                const st = getVideoStatus(v.id)
                                return (
                                    <div
                                        key={v.id}
                                        className={`module-row ${currentVideo?.id === v.id ? 'current' : ''}`}
                                        onClick={() => setCurrentVideo(v)}
                                    >
                                        <div className={`module-icon ${st}`}>
                                            {st === 'done' ? '✓' : st === 'current' ? '▶' : v.order + 1}
                                        </div>
                                        <span className="module-title">{v.title}</span>
                                        <span className="module-time">
                                            {v.durationSec < 60
                                                ? `${Math.ceil(v.durationSec)}s`
                                                : (() => {
                                                    const totalSeconds = Math.ceil(v.durationSec);
                                                    const minutes = Math.floor(totalSeconds / 60);
                                                    const seconds = totalSeconds % 60;

                                                    return seconds > 0
                                                        ? `${minutes}m ${seconds}s`
                                                        : `${minutes}m`;
                                                })()}
                                        </span>
                                    </div>
                                )
                            })}
                        </div>

                        {quiz && (
                            <div className="quiz-cta">
                                <div className="quiz-icon">✦</div>
                                <h4 className="serif">{quiz.title}</h4>
                                <p>{allDone ? 'Você completou todos os vídeos. Está pronto para o quiz!' : 'Conclua todos os vídeos para liberar o quiz.'}</p>
                                <button
                                    className={`btn btn-full ${allDone ? 'btn-primary' : 'btn-ghost'}`}
                                    disabled={!allDone}
                                    onClick={() => navigate(`/quiz/${quiz.id}`)}
                                >
                                    {allDone ? 'Fazer o quiz →' : 'Quiz bloqueado'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    )
}